/**
 * Ted #26 (#987) — the confirm flow, shared by the Bookings page and Front
 * Desk so both prompt, batch and word failures identically (Principle 1:
 * one truth). The dialog is the view; this is the decision.
 *
 * THE RULE THIS ENCODES, from Ted on 2026-09-15:
 *   *"reduce duplicate work, but never remove staff control."*
 *
 * Which resolves into exactly two paths:
 *
 *   1. The dog (household) has NO other pending reservation → confirm it
 *      immediately through `POST /api/bookings/admin/:id/confirm`. One tap,
 *      no dialog, nothing to review. Notification behaviour is that route's,
 *      unchanged: the customer gets the confirmation + 24h reminder.
 *   2. There ARE others → open the review dialog. Nothing is confirmed until
 *      the staffer acts, and the batch applies through
 *      `POST /api/bookings/admin/confirm-batch`, which honours the notify
 *      toggle and answers PER ITEM, so one refusal never costs the siblings.
 *
 * The lookup that decides between them is one `/schedule` call filtered to
 * `status=pending` over a wide window — the same call the pending queue view
 * uses. A pending reservation in the PAST is not a mistake to hide: HTHD
 * carries five of them (Phase 1 ground truth §5), and the approval queue
 * (#923) is exactly where they should surface.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  confirmBooking,
  confirmBookingsBatch,
  describeConfirmRefusal,
  fetchAdminSchedule,
  humanizeApiError,
  readSession,
  todayIsoDate,
  type AdminBooking,
} from '../../lib/api.js';
import {
  confirmFailureBanner,
  isPastReservation,
  pendingForHousehold,
  reservationLabel,
  summarizeConfirmOutcome,
  toPendingReservation,
  type ConfirmOutcomeBanner,
  type PendingReservation,
} from './confirm-pending.js';
import type { ConfirmPendingDialogProps } from './ConfirmPendingDialog.js';
import { effectiveRolesOf, rolesHavePermission } from '../../lib/rbac-matrix.js';

/** LG (#1114 AC 8) — the same string the server checks (booking/gates/review-hold.ts). */
export const REVIEW_HOLD_OVERRIDE_PERMISSION = 'bookings.review-hold:override:all';

/**
 * How far either side of today the sibling lookup (and the pending queue)
 * reaches. Backwards far enough to surface reservations that were never
 * actioned; forwards a year, which is past any real booking horizon.
 */
export const PENDING_LOOKBACK_DAYS = 180;
export const PENDING_LOOKAHEAD_DAYS = 365;

export function isoOffsetDays(days: number, from: Date = new Date()): string {
  const d = new Date(from.getTime());
  d.setDate(d.getDate() + days);
  return todayIsoDate(d);
}

export function pendingWindow(): { startDate: string; endDate: string } {
  return {
    startDate: isoOffsetDays(-PENDING_LOOKBACK_DAYS),
    endDate: isoOffsetDays(PENDING_LOOKAHEAD_DAYS),
  };
}

/** What a surface knows about the row the staffer tapped Confirm on. */
export interface ConfirmAnchor {
  bookingId: string;
  customerId: string;
  customerName: string | null;
  /** Used only when the wider lookup cannot return the row itself. */
  fallback: PendingReservation;
}

export interface UseConfirmPendingResult {
  /** Tapping Confirm on a row. Resolves when the dialog opens or the
   *  one-tap confirm lands. */
  begin: (anchor: ConfirmAnchor) => Promise<void>;
  /** Rows with a confirm in flight — the surface disables their buttons. */
  busyIds: ReadonlySet<string>;
  /** Props for `<ConfirmPendingDialog>`, or null when it is closed. */
  dialogProps: ConfirmPendingDialogProps | null;
  /**
   * What happened, for the page's own banner. A toast is not a record
   * (DESIGN-PHILOSOPHY anti-patterns) — the outcome names every reservation
   * that was left pending and why, and carries the TONE it should be said in
   * so a refusal can never be painted as a success.
   */
  banner: ConfirmOutcomeBanner | null;
  dismissBanner: () => void;
}

interface DialogState {
  anchor: ConfirmAnchor;
  items: PendingReservation[];
  loading: boolean;
  loadError: string | null;
}

export function useConfirmPending(args: {
  apiUrl: string;
  /** The customer-facing service name for a booking row. */
  serviceLabelForBooking: (booking: AdminBooking) => string;
  /** Called after any confirm lands so the surface can refresh. */
  onApplied: () => void;
  /**
   * Sawyer #1296 — the reservations the server actually confirmed, after it
   * did (refusals excluded), so the surface can ask "Add this to the owner's
   * notes?". Nothing here waits on it: the confirm has already landed.
   */
  onConfirmed?: (items: PendingReservation[]) => void;
}): UseConfirmPendingResult {
  const { apiUrl, serviceLabelForBooking, onApplied } = args;
  const onConfirmedRef = useRef(args.onConfirmed);
  onConfirmedRef.current = args.onConfirmed;

  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  // LG — the manager's reason for confirming HELD rows (board Contracts §3).
  const [overrideReason, setOverrideReason] = useState('');
  const { staff } = readSession();
  const canOverrideHold = rolesHavePermission(effectiveRolesOf(staff), REVIEW_HOLD_OVERRIDE_PERMISSION);
  const [applying, setApplying] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [banner, setBanner] = useState<ConfirmOutcomeBanner | null>(null);

  const markBusy = useCallback((id: string, busy: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  /** The household's pending reservations, oldest first, anchor included. */
  const lookupSiblings = useCallback(
    async (anchor: ConfirmAnchor): Promise<PendingReservation[]> => {
      const { token } = readSession();
      if (!token) throw new Error('unauthorized');
      const window = pendingWindow();
      // Scoped to the ANCHOR'S HOUSEHOLD server-side. The first cut asked
      // for every pending row in the tenant across 545 days and filtered by
      // customer in the browser: correct, tenant-scoped, and a full
      // tenant-wide list — dogs joined, every row serialized — on every
      // Confirm tap at the counter. The grouping rule is unchanged and still
      // applied below; this only stops fetching what it was about to discard.
      const rows = await fetchAdminSchedule(apiUrl, token, {
        ...window,
        status: 'pending',
        customerId: anchor.customerId,
      });
      const fetchedAnchor = rows.find((b) => b.id === anchor.bookingId);
      if (!fetchedAnchor) {
        // The row is pending on screen but absent from the window (it can sit
        // outside it, or it was just actioned elsewhere). Offer what we know
        // rather than dropping the staffer's tap on the floor.
        return [anchor.fallback];
      }
      return pendingForHousehold(fetchedAnchor, rows).map((b) =>
        toPendingReservation(b, serviceLabelForBooking(b)),
      );
    },
    [apiUrl, serviceLabelForBooking],
  );

  const begin = useCallback(
    async (anchor: ConfirmAnchor): Promise<void> => {
      setBanner(null);
      setActionError(null);
      markBusy(anchor.bookingId, true);
      let items: PendingReservation[];
      try {
        items = await lookupSiblings(anchor);
      } catch (err) {
        // Path 2 with a warning: we could not prove there are no siblings, so
        // we must NOT silently batch — and must not silently swallow the tap
        // either. The dialog opens with the one reservation and a Try again.
        markBusy(anchor.bookingId, false);
        setSelected(new Set([anchor.bookingId]));
        setDialog({
          anchor,
          items: [anchor.fallback],
          loading: false,
          loadError: humanizeApiError(err instanceof Error ? err.message : null),
        });
        return;
      }

      // LG — a HELD row is never one tap: the server refuses it without a
      // manager's reason, and the dialog is where the reason (and the
      // reason for the hold) are shown. Open it even for a lone reservation.
      const anchorHeld = items.some((i) => i.bookingId === anchor.bookingId && i.reviewHoldReason);
      if (items.length <= 1 && !anchorHeld) {
        // Path 1 — one tap. The single-booking route, which is the one the
        // admin app could never call until this change.
        try {
          const { token } = readSession();
          if (!token) throw new Error('unauthorized');
          await confirmBooking(apiUrl, token, anchor.bookingId);
          setBanner(summarizeConfirmOutcome({ confirmedCount: 1, refusals: [] }));
          onApplied();
          onConfirmedRef.current?.([
            items.find((i) => i.bookingId === anchor.bookingId) ?? anchor.fallback,
          ]);
        } catch (err) {
          setBanner(
            confirmFailureBanner(
              reservationLabel(anchor.fallback),
              humanizeApiError(err instanceof Error ? err.message : null),
            ),
          );
        } finally {
          markBusy(anchor.bookingId, false);
        }
        return;
      }

      // Path 2 — review first. Everything ticked: the common answer is "yes,
      // all of them", and that must be one more tap, not five.
      markBusy(anchor.bookingId, false);
      // Ted 10-09 (#915) — the queue reaches 180 days back. When the list
      // includes a visit that already happened, the box starts OFF: nobody
      // wants "Your booking is confirmed!" about last Tuesday. (The server
      // refuses to send that email anyway; this keeps the box honest.)
      const today = todayIsoDate();
      setNotifyCustomer(!items.some((i) => isPastReservation(i, today)));
      setOverrideReason('');
      setSelected(new Set(items.map((i) => i.bookingId)));
      setDialog({ anchor, items, loading: false, loadError: null });
    },
    [apiUrl, lookupSiblings, markBusy, onApplied],
  );

  const retryLookup = useCallback(async (): Promise<void> => {
    if (!dialog) return;
    const anchor = dialog.anchor;
    setDialog({ ...dialog, loading: true, loadError: null });
    try {
      const items = await lookupSiblings(anchor);
      setSelected(new Set(items.map((i) => i.bookingId)));
      setDialog({ anchor, items, loading: false, loadError: null });
    } catch (err) {
      setDialog({
        anchor,
        items: [anchor.fallback],
        loading: false,
        loadError: humanizeApiError(err instanceof Error ? err.message : null),
      });
    }
  }, [dialog, lookupSiblings]);

  const applyBatch = useCallback(
    async (bookingIds: string[]): Promise<void> => {
      if (!dialog || bookingIds.length === 0) return;
      setApplying(true);
      setActionError(null);
      try {
        const { token } = readSession();
        if (!token) throw new Error('unauthorized');
        const heldInBatch = dialog.items.some((i) => bookingIds.includes(i.bookingId) && i.reviewHoldReason);
        const outcome = await confirmBookingsBatch(apiUrl, token, {
          bookingIds,
          notifyCustomer,
          // Sent only when a held row is in the batch AND the caller may override;
          // the server audits each cleared hold with this reason.
          ...(heldInBatch && canOverrideHold && overrideReason.trim().length >= 3
            ? { overrideReason: overrideReason.trim() }
            : {}),
        });
        const byId = new Map(dialog.items.map((i) => [i.bookingId, i]));
        const refusals = outcome.results
          .filter((r) => r.outcome === 'refused')
          .map((r) => {
            const item = byId.get(r.bookingId);
            return {
              label: item ? reservationLabel(item) : 'One reservation',
              reason: describeConfirmRefusal(r.refusal),
            };
          });
        setBanner(
          summarizeConfirmOutcome({
            confirmedCount: outcome.confirmedCount,
            refusals,
          }),
        );
        setDialog(null);
        onApplied();
        // Only what the server says it CONFIRMED is asked about.
        const confirmedIds = new Set(
          outcome.results.filter((r) => r.outcome === 'confirmed').map((r) => r.bookingId),
        );
        const confirmed = bookingIds
          .filter((id) => confirmedIds.has(id))
          .map((id) => byId.get(id))
          .filter((i): i is PendingReservation => i !== undefined);
        if (confirmed.length > 0) onConfirmedRef.current?.(confirmed);
      } catch (err) {
        // A request-level failure (network, 403, malformed) — nothing was
        // confirmed. Stay open so the staffer can retry without re-reviewing.
        setActionError(humanizeApiError(err instanceof Error ? err.message : null));
      } finally {
        setApplying(false);
      }
    },
    [apiUrl, canOverrideHold, dialog, notifyCustomer, onApplied, overrideReason],
  );

  const dialogProps = useMemo<ConfirmPendingDialogProps | null>(() => {
    if (!dialog) return null;
    return {
      anchorId: dialog.anchor.bookingId,
      customerName: dialog.anchor.customerName,
      items: dialog.items,
      selected,
      onToggle: (bookingId: string) =>
        setSelected((prev) => {
          const next = new Set(prev);
          if (next.has(bookingId)) next.delete(bookingId);
          else next.add(bookingId);
          return next;
        }),
      loading: dialog.loading,
      loadError: dialog.loadError,
      onRetryLoad: () => void retryLookup(),
      notifyCustomer,
      onNotifyChange: setNotifyCustomer,
      overrideReason,
      onOverrideReasonChange: setOverrideReason,
      canOverrideHold,
      busy: applying,
      actionError,
      onConfirmAnchorOnly: () => void applyBatch([dialog.anchor.bookingId]),
      onConfirmSelected: () =>
        void applyBatch(
          dialog.items
            .map((i) => i.bookingId)
            .filter((id) => selected.has(id)),
        ),
      onDismiss: () => {
        setDialog(null);
        setActionError(null);
      },
    };
  }, [applyBatch, actionError, applying, canOverrideHold, dialog, notifyCustomer, overrideReason, retryLookup, selected]);

  return {
    begin,
    busyIds,
    dialogProps,
    banner,
    dismissBanner: () => setBanner(null),
  };
}
